import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const FETCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const ZIP_MAX_LENGTH = 5;
const ALLOWED_PRIMARY_TYPES = ["postal_code"];
const PLACE_DETAIL_FIELD_MASK = "addressComponents,location,formattedAddress";
const PLACES_API_BASE = "https://places.googleapis.com/v1";

/* ─────────────────────── REST helpers (Places API New) ──────────────────────── */

function getApiKey() {
  return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
}

function makeSessionToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

async function fetchAutocomplete({ input, sessionToken, signal }) {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set");
  const res = await fetch(`${PLACES_API_BASE}/places:autocomplete`, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
    },
    body: JSON.stringify({
      input,
      sessionToken,
      includedRegionCodes: ["us"],
      includedPrimaryTypes: ALLOWED_PRIMARY_TYPES,
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Autocomplete ${res.status}${body ? `: ${body}` : ""}`);
  }
  const data = await res.json();
  return Array.isArray(data?.suggestions) ? data.suggestions : [];
}

async function fetchPlaceDetails({ placeId, sessionToken, signal }) {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set");
  const url = new URL(`${PLACES_API_BASE}/places/${encodeURIComponent(placeId)}`);
  if (sessionToken) url.searchParams.set("sessionToken", sessionToken);
  const res = await fetch(url.toString(), {
    method: "GET",
    signal,
    headers: {
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask": PLACE_DETAIL_FIELD_MASK,
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Place details ${res.status}${body ? `: ${body}` : ""}`);
  }
  return await res.json();
}

/* ───────────────────── Prediction / place parsing helpers ───────────────────── */

function predictionMain(p) {
  return p?.structuredFormat?.mainText?.text || p?.text?.text || "";
}

function predictionSecondary(p) {
  return p?.structuredFormat?.secondaryText?.text || "";
}

function componentText(c, preferLong = true) {
  if (!c) return "";
  return (preferLong ? c.longText : c.shortText) || c.longText || c.shortText || "";
}

function placeToZipAddress(place) {
  const components = place?.addressComponents || [];
  let neighborhood = "";
  let sublocality = "";
  let locality = "";
  let state = "";
  let zip = "";
  for (const component of components) {
    const types = component.types || [];
    for (const t of types) {
      switch (t) {
        case "postal_code":
          zip = componentText(component);
          break;
        case "locality":
          locality = componentText(component, false);
          break;
        case "sublocality_level_1":
          sublocality = componentText(component, false);
          break;
        case "neighborhood":
          neighborhood = componentText(component, false);
          break;
        case "administrative_area_level_1":
          state = componentText(component, false);
          break;
        default:
          break;
      }
    }
  }
  const city = locality || sublocality || neighborhood;
  return { street: "", city, state, zip };
}

function placeToCoordinates(place) {
  const loc = place?.location;
  if (!loc) return null;
  if (loc.latitude == null || loc.longitude == null) return null;
  return { lat: loc.latitude, lng: loc.longitude };
}

function formatZipDescription(parts) {
  return `${parts.city} ${parts.state} (${parts.zip})`;
}

function normalizeZipDigits(raw) {
  return String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, ZIP_MAX_LENGTH);
}

/* ─────────────────────────────── Component ──────────────────────────────────── */

export default function PlacesAutocomplete(props) {
  const { onChange, helperText, required = false } = props;

  const initialZip = typeof props.value === "object" ? normalizeZipDigits(props.value?.zip) : "";
  const initialDescription = typeof props.value === "object" ? props.value?.description || "" : "";

  const [isOnline, setIsOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine
  );
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  const [inputValue, setInputValue] = useState(initialZip);
  const [isSelected, setIsSelected] = useState(Boolean(initialDescription));
  const [resolvedDescription, setResolvedDescription] = useState(initialDescription);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasRequestError, setHasRequestError] = useState(false);

  const sessionTokenRef = useRef(null);
  const abortRef = useRef(null);
  const debounceTimerRef = useRef(null);
  /** Synchronous mirror of `inputValue` so the blur handler sees the just-typed value
   *  (state updates are batched but blur fires in the same tick). */
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const sync = () => {
      const on = typeof navigator !== "undefined" && navigator.onLine;
      isOnlineRef.current = on;
      setIsOnline(on);
    };
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, []);

  /** Initial `value` applies only on first mount. To reset from the parent,
   * remount with a different `key` or drive updates via `onChange`. */

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = makeSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const runFetch = useCallback(
    async (input) => {
      const tooShort = !input || input.length < MIN_QUERY_LENGTH;
      if (tooShort || !isOnlineRef.current) {
        setOptions([]);
        setLoading(false);
        return;
      }
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setLoading(true);
      setHasRequestError(false);
      try {
        const suggestions = await fetchAutocomplete({
          input,
          sessionToken: ensureSessionToken(),
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        setOptions(suggestions.map((s) => s.placePrediction).filter(Boolean));
      } catch (err) {
        if (err?.name === "AbortError") return;
        setOptions([]);
        setHasRequestError(true);
        if (typeof navigator === "undefined" || navigator.onLine) {
          console.error("[PlacesAutocomplete] autocomplete failed", err);
        }
      } finally {
        if (abortRef.current === ac) {
          abortRef.current = null;
          setLoading(false);
        }
      }
    },
    [ensureSessionToken]
  );

  const resetField = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    inputValueRef.current = "";
    setInputValue("");
    setIsSelected(false);
    setResolvedDescription("");
    setOptions([]);
    setLoading(false);
    setHasRequestError(false);
    onChange("");
  }, [onChange]);

  /** Commit a typed 5-digit zip with no city/state (used when the user presses Enter
   *  without suggestions). */
  const commitBareZip = useCallback(
    (zip) => {
      setIsSelected(true);
      setResolvedDescription("");
      onChange({
        street: "",
        city: "",
        state: "",
        zip,
        coord: null,
        description: zip,
      });
    },
    [onChange]
  );

  const applyPlacePrediction = useCallback(
    async (picked) => {
      const placeId = picked?.placeId;
      if (!placeId) return;

      setIsSelected(true);
      const previewZip = normalizeZipDigits(predictionMain(picked));
      if (previewZip) {
        inputValueRef.current = previewZip;
        setInputValue(previewZip);
      }

      try {
        const place = await fetchPlaceDetails({
          placeId,
          sessionToken: ensureSessionToken(),
        });
        const address = placeToZipAddress(place);
        const zipNorm = normalizeZipDigits(address.zip);
        const addressNorm = { ...address, zip: zipNorm };
        const coord = placeToCoordinates(place);
        const description = formatZipDescription(addressNorm);
        sessionTokenRef.current = null;
        inputValueRef.current = zipNorm;
        setInputValue(zipNorm);
        setResolvedDescription(description);
        onChange({ ...addressNorm, coord, description });
      } catch (err) {
        console.error("[PlacesAutocomplete] place details failed", err);
      }
    },
    [ensureSessionToken, onChange]
  );

  useEffect(() => {
    if (!isOnline || !open) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      runFetch(inputValue);
    }, FETCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [inputValue, isOnline, open, runFetch]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleSelect = async (_e, picked) => {
    if (picked == null) return;
    if (typeof picked === "string") {
      const v = normalizeZipDigits(picked);
      if (optionsRef.current.length > 0) {
        await applyPlacePrediction(optionsRef.current[0]);
        return;
      }
      if (v.length === ZIP_MAX_LENGTH) commitBareZip(v);
      return;
    }
    await applyPlacePrediction(picked);
  };

  const handleSearchClick = () => {
    if (!inputValue || inputValue.length < MIN_QUERY_LENGTH) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setOpen(true);
    runFetch(inputValue);
  };

  const showOffline = !isOnline;
  const showRequestError = isOnline && hasRequestError && options.length === 0;
  const showSearchAdornment = !isSelected && inputValue.length > 0;
  const showNoMatches =
    isOnline &&
    !loading &&
    !hasRequestError &&
    !isSelected &&
    inputValue.length >= MIN_QUERY_LENGTH &&
    options.length === 0;

  /** Show resolved "City State (Zip)" when a selection exists, else fall back to
   *  the parent-supplied `helperText`. Suppressed while error/no-match lines show. */
  const helperPrimary =
    !showOffline && !showRequestError && !showNoMatches
      ? resolvedDescription || helperText || ""
      : "";

  return (
    <div className="autocomplete-container">
      <Box mb={0}>
        <Autocomplete
          freeSolo
          fullWidth
          autoComplete
          includeInputInList
          open={open}
          onOpen={() => setOpen(true)}
          onClose={(_e, reason) => {
            setOpen(false);
            if (reason !== "blur" || isSelected) return;
            if (optionsRef.current.length > 0) {
              void applyPlacePrediction(optionsRef.current[0]);
            } else {
              resetField();
            }
          }}
          options={options}
          loading={loading}
          filterOptions={(x) => x}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : predictionMain(option)
          }
          isOptionEqualToValue={(option, val) =>
            Boolean(option?.placeId && val?.placeId && option.placeId === val.placeId)
          }
          inputValue={inputValue}
          onInputChange={(_e, raw, reason) => {
            if (reason === "reset") return;
            const v = normalizeZipDigits(raw);
            if (!v) {
              resetField();
              return;
            }
            inputValueRef.current = v;
            setInputValue(v);
            setIsSelected(false);
            setResolvedDescription("");
            setHasRequestError(false);
            setOpen(true);
            if (v.length >= MIN_QUERY_LENGTH) {
              setLoading(true);
            } else {
              setLoading(false);
              setOptions([]);
            }
            // Don't commit while typing — wait for an explicit selection,
            // Enter, or blur. This keeps the dropdown open at digit 5.
            onChange("");
          }}
          onChange={handleSelect}
          loadingText={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "text.secondary",
              }}
            >
              <CircularProgress color="inherit" size={14} />
              Searching zip codes…
            </Box>
          }
          renderInput={(params) => (
            <TextField
              {...params}
              required={required}
              name="zip"
              variant="outlined"
              label="Zip Code"
              inputProps={{
                ...params.inputProps,
                autoComplete: "off",
                inputMode: "numeric",
                maxLength: ZIP_MAX_LENGTH,
                pattern: "[0-9]*",
              }}
              sx={(t) => ({
                "& .MuiOutlinedInput-root": {
                  minHeight: t.spacing(7),
                  height: t.spacing(7),
                },
              })}
              InputProps={{
                ...params.InputProps,
                endAdornment:
                  loading || showSearchAdornment ? (
                    <Box
                      className="MuiAutocomplete-endAdornment"
                      sx={{
                        position: "absolute",
                        right: 9,
                        top: "calc(50% - 14px)",
                      }}
                    >
                      {loading ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : (
                        <IconButton aria-label="search" size="small" onClick={handleSearchClick}>
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ) : (
                    params.InputProps.endAdornment
                  ),
              }}
            />
          )}
          renderOption={(optionProps, option) => {
            const main = predictionMain(option);
            const secondary = predictionSecondary(option);
            return (
              <li {...optionProps} key={option.placeId || main}>
                <Box>
                  <Box sx={{ fontWeight: 500 }}>{main}</Box>
                  {secondary ? (
                    <Box sx={{ color: "text.secondary", fontSize: 12 }}>{secondary}</Box>
                  ) : null}
                </Box>
              </li>
            );
          }}
        />
        {helperPrimary ? (
          <FormHelperText sx={{ mt: 0.5, mx: 0 }}>{helperPrimary}</FormHelperText>
        ) : null}
        {showOffline && (
          <FormHelperText sx={{ mt: 0.5 }} error={true}>
            No internet. Please restore your data connection.
          </FormHelperText>
        )}
        {showRequestError && (
          <FormHelperText sx={{ mt: 0.5 }} error={true}>
            Unable to load suggestions. Please try again.
          </FormHelperText>
        )}
        {showNoMatches && (
          <FormHelperText sx={{ mt: 0.5 }}>No matching zip codes found.</FormHelperText>
        )}
      </Box>
    </div>
  );
}
